import { useEffect, useRef, useState } from 'react';
import { armAudio, playBell, BELL_SOUNDS, type BellSound } from '../openingBell';

const ENABLED_KEY = 'chronos-opening-bell';
const SOUND_KEY = 'chronos-opening-bell-sound';

/** Weekday + minutes-since-midnight in America/New_York */
function etNow(): { day: number; mins: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour12: false,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date());
  const get = (t: string) => parts.find((p) => p.type === t)?.value || '';
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const hour = parseInt(get('hour'), 10) % 24;
  return { day: days.indexOf(get('weekday')), mins: hour * 60 + parseInt(get('minute'), 10) };
}

const OPEN_MINS = 9 * 60 + 30; // 09:30 ET

function storedSound(): BellSound {
  const s = localStorage.getItem(SOUND_KEY) as BellSound | null;
  return s && BELL_SOUNDS.some((b) => b.key === s) ? s : 'real';
}

/**
 * 🔔 header control: opens a menu of market-open sounds (all Web Audio
 * synthesis). Picking one arms the bell (the click satisfies the browser's
 * autoplay-gesture rule) and previews it; Off silences. While armed and the
 * tab is open, the chosen sound plays when the ET clock crosses 09:30 on a
 * weekday — transition-detected, so loading the page mid-session is silent.
 */
export function OpeningBell() {
  const [enabled, setEnabled] = useState(() => localStorage.getItem(ENABLED_KEY) === '1');
  const [sound, setSound] = useState<BellSound>(storedSound);
  const [open, setOpen] = useState(false);
  const wasOpen = useRef<boolean | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!enabled) return;
    armAudio();
    const tick = () => {
      const { day, mins } = etNow();
      const marketOpen = day >= 1 && day <= 5 && mins >= OPEN_MINS && mins < 16 * 60;
      if (wasOpen.current === false && marketOpen) playBell(storedSound());
      wasOpen.current = marketOpen;
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [enabled]);

  // Close the menu on any outside click
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  const choose = (key: BellSound) => {
    setSound(key);
    localStorage.setItem(SOUND_KEY, key);
    setEnabled(true);
    localStorage.setItem(ENABLED_KEY, '1');
    armAudio(); // this click is the required user gesture
    playBell(key, true); // preview the pick
    setOpen(false);
  };

  const turnOff = () => {
    setEnabled(false);
    localStorage.setItem(ENABLED_KEY, '0');
    setOpen(false);
  };

  const current = BELL_SOUNDS.find((b) => b.key === sound);

  return (
    <div className="bell-root" ref={rootRef}>
      <button
        className={`bell-toggle${enabled ? ' active' : ''}`}
        onClick={() => setOpen((o) => !o)}
        title={
          enabled
            ? `Opening bell ON (${current?.label}) — rings at 9:30 ET while this tab is open`
            : 'Choose a 9:30 ET opening bell sound'
        }
      >
        {enabled ? '🔔' : '🔕'} ▾
      </button>

      {open && (
        <div className="bell-menu">
          <div className="bell-menu-title">Opening bell · 9:30 ET</div>
          {BELL_SOUNDS.map((b) => (
            <button
              key={b.key}
              className={`bell-menu-item${enabled && sound === b.key ? ' active' : ''}`}
              onClick={() => choose(b.key)}
            >
              {enabled && sound === b.key ? '🔔' : '·'} {b.label}
            </button>
          ))}
          <button className="bell-menu-item bell-menu-off" onClick={turnOff}>
            {enabled ? '·' : '🔕'} Off
          </button>
        </div>
      )}
    </div>
  );
}
